import { type FC, type ReactNode, useCallback, useEffect } from "react";
import { useUserStore } from "@/stores/useUserStore.ts";
import { useSignalStore } from "@/stores/useSignalrStore.ts";
import { InternalSigRErrors, SignalWrapper } from "@/helpers/signalr/signalr.ts";
import { HubConnectionState, LogLevel } from "@microsoft/signalr";
import { toastAndError, toastAndWarn } from "@/helpers/toast.ts";
import { log } from "@/helpers/console.ts";
import { SignalInvoke, SignalOn } from "@/types/signalr-functions.ts";
import { toast } from "sonner";
import { TutorStatus } from "@/types/tutor.ts";
import { PRODUCTION, SIG_FLOW_ENDPOINT, SIG_HUB_NAME } from "@/constants/settings.ts";
import { useSettingsStore } from "@/stores/useSettingsStore.ts";

interface SignalRProps {
  children?: ReactNode;
}

type NegotiateJson = {
  negotiateVersion: number;
  url: string;
  accessToken: string;
  availableTransports: Array<string>;
};

export const SignalRWrapper: FC<SignalRProps> = ({ children }) => {
  const { settings } = useSettingsStore();
  const {
    Startup,
    auth,
    userId,
    tutorId,
    valid,
    Logout,
    SetStatus: SetUserStatus,
  } = useUserStore();
  const { Create, Remove, SetStatus, signalr } = useSignalStore();

  const DebugSigR = !settings.MiscSignalRDebug ? LogLevel.Error : LogLevel.Debug;

  const signalrDetails: () => Promise<NegotiateJson | null> = useCallback(async () => {
    const negotiate = await fetch(`${SIG_FLOW_ENDPOINT}/${SIG_HUB_NAME}/negotiate`, {
      method: "GET",
      headers: {
        "x-ms-signalr-userid": userId.toString(),
        Authorization: `Bearer ${auth.token}`,
      },
    });

    if (!negotiate.ok) {
      console.log("Negotiate failed: ", negotiate);
      return null;
    }

    return await negotiate.json();
  }, [userId, auth.token]);

  const connectSigR = useCallback(
    (json: NegotiateJson) => {
      const sig = new SignalWrapper<SignalOn, SignalInvoke>()
        .withUrl(json.url)
        .withToken(json.accessToken)
        .withLock()
        .withBrowserOfflineCheck()
        // .withWebWorker() // Not working yet
        .withLogLevel(DebugSigR)
        .withStatusCallback((state) => {
          SetStatus(state);
          console.info("SignalR state changed!", state);

          switch (state) {
            case HubConnectionState.Connected:
              toast.success("Connection to SignalR successful");

              break;
            case HubConnectionState.Disconnected:
              toast.warning("Connection to SignalR lost, attempting to reconnect");
              SetUserStatus(TutorStatus.Offline);
              break;

            default:
              toast.info("SignalR state changed to: " + state);
              break;
          }
        })
        .withErrorCallback((err) => {
          SetStatus(HubConnectionState.Disconnected);
          SetUserStatus(TutorStatus.Offline);
          if (err.isSome()) {
            const error = err.unwrap();

            switch (error.statusCode) {
              case 401:
                // If its successful, the new details should be used next refetch
                signalrDetails().catch(() => {
                  Logout();
                });
                break;

              case InternalSigRErrors.WebLockAlreadyAcquired:
                toast.error(
                  "Warning: You already have another tutor dashboard tab open. This may cause issues. Please close duplicate tabs and refresh the page!",
                  {
                    duration: 9999999,
                    closeButton: true,
                  },
                );
                break;

              case InternalSigRErrors.BrowserOffline:
                toast.error(
                  "Your browser reported that its no longer connected to the internet :(",
                  {
                    duration: 9999999,
                    closeButton: true,
                  },
                );
                break;

              default:
                toastAndError("SigR error callback: " + error.message, error.statusCode);
                break;
            }
          }
        })
        .withDebugSetup()
        .withOnStartup(() => {
          Create(sig);

          sig
            .Invoke(SignalInvoke.JoinLessonDashboardGroup)
            .then(() => {
              log("Joined lesson dashboard group");
            })
            .catch((e) => toastAndError("Unable to connect to dashboard group", e));
        });

      if (settings.MiscSignalRDebug) sig.withMessagePack();

      sig.StartWithAutoConnect(1000 * 10);

      return () => {
        sig.Dispose().catch(() => toastAndWarn("Unable to dispose of SignalR, this could be bad"));
        Remove();
      };
    },
    [Logout, DebugSigR, Create, SetUserStatus, SetStatus, settings, Remove, signalrDetails],
  );

  useEffect(() => {
    if (!PRODUCTION) {
      if (import.meta.hot) {
        // TLDR: Free the lock before we reload the page as to avoid web-lock spam messages when developing locally
        const freeLock = () => {
          signalr?.FreeLock();
        };

        const wsDisconnectAlert = () => {
          toast.warning("Vite websocket disconnected, hot reloading may not work");
        };

        const wsConnectedAlert = () => {
          toast.success("Vite websocket connected");
        };

        import.meta.hot.on("vite:beforeUpdate", freeLock);
        import.meta.hot.on("vite:ws:disconnect", wsDisconnectAlert);
        import.meta.hot.on("vite:ws:connect", wsConnectedAlert);

        return () => {
          if (import.meta.hot) {
            import.meta.hot.off("vite:beforeUpdate", freeLock);
            import.meta.hot.off("vite:ws:disconnect", wsDisconnectAlert);
            import.meta.hot.off("vite:ws:connect", wsConnectedAlert);
          }
        };
      }
    }
  }, [signalr]);

  useEffect(() => {
    Startup();
  }, [Startup]);

  useEffect(() => {
    if (valid && userId !== -1 && tutorId !== -1) {
      signalrDetails().then((data) => {
        if (data === null) return;

        let removeCallback = connectSigR(data);

        return () => {
          removeCallback();
        };
      });
    }

    if (valid === false && !window.location.pathname.includes("/auth/"))
      window.location.assign("/auth/login");
  }, [auth, userId, tutorId, valid, connectSigR, signalrDetails]);

  return <>{children}</>;
};
